import { AdviseCurrentSuppliers, AdviseFeatureInput, MatrixCriterion } from '../../../../types/advise';
import { selectedFeatures } from '../../../../utils/adviseMatrix';

export type StepStatus = 'not_started' | 'in_progress' | 'complete';

export const STEP_STATUS_LABELS: Record<StepStatus, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  complete: 'Complete'
};

// Complete once a selected criterion has a ticked feature: the sheets and the report have something to ask about.
export const functionalityMatrixStepStatus = (criteria: MatrixCriterion[] | null): StepStatus | undefined => {
  if (!criteria) return undefined;
  return selectedFeatures(criteria).length > 0 ? 'complete' : 'not_started';
};

// Complete once every shortlisted supplier's response is imported with no flagged answer left to review; a shortlist
// has no status until the answers load. `awaiting_review` is sent to every reader, so the client sees the same.
export const supplierSelectionStepStatus = (
  selected?: string[] | null,
  featureInput?: AdviseFeatureInput | null
): StepStatus | undefined => {
  if (!selected || selected.length === 0) return 'not_started';
  if (!featureInput) return undefined;
  const done = new Set(
    featureInput.suppliers.filter((supplier) => supplier.imported_at && !supplier.awaiting_review).map(({ id }) => id)
  );
  return selected.every((id) => done.has(id)) ? 'complete' : 'in_progress';
};

// Official Quotes and Pricing share the step, complete once a quote is uploaded.
export const quotesPricingStepStatus = (quotes?: unknown[] | null): StepStatus =>
  quotes && quotes.length > 0 ? 'complete' : 'not_started';

// Complete once any file is uploaded, to the room or to its notes; never In Progress.
export const artifactsStepStatus = (files?: unknown[] | null, noteFiles?: unknown[] | null): StepStatus =>
  (files?.length ?? 0) + (noteFiles?.length ?? 0) > 0 ? 'complete' : 'not_started';

export const currentSuppliersStepStatus = (current?: AdviseCurrentSuppliers | null): StepStatus => {
  if (!current) return 'not_started';
  if (current.skipped) return 'complete';
  const suppliers = current.suppliers ?? [];
  if (suppliers.length === 0) return 'not_started';
  return suppliers.every((supplier) => supplier.complete) ? 'complete' : 'in_progress';
};
